import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { datumAusSpalten, datumKurz, jahrKurz, nachGeburt } from "@stammbaum/shared";
import type { UnscharfesDatum } from "@stammbaum/shared";
import { bildUrl, type PersonKurz } from "../api";
import { usePersonKarte, type Ausloeser } from "./PersonKarte";
import { umfeldKarte } from "./umfeld";
import type { BaumDaten } from "./baum-layout";

/**
 * Die Verwandtschaft auf der Personenseite.
 *
 * Gruppiert nach RICHTUNG, nicht nach Bezeichnung. Vorher bekam jede
 * Bezeichnung eine eigene Ueberschrift - bei fuenfzehn Bezeichnungen
 * fuenfzehn Ueberschriften, und darunter Kacheln mit Bild, von denen jede
 * eine Zeilenhoehe plus Rand kostete. Zusammen war das der laengste
 * Abschnitt der Seite, obwohl er nur eine Navigationshilfe ist.
 *
 * Jetzt vier Gruppen, darin eine Zeile je Person und die genaue
 * Bezeichnung klein daneben. Zwei Spalten, damit Mutter und Vater
 * nebeneinander stehen: sie sind die naechsten Vorfahren und stehen durch
 * die Sortierung vorn. (Bei einer Person, die in mehreren Familien Kind
 * ist - leiblich und adoptiert -, sind es mehr als zwei; dann verschiebt
 * sich das Paar, die Reihenfolge bleibt aber richtig.)
 *
 * Keine Tabelle: eine Tabelle verspricht, dass ihre Spalten ueber alle
 * Zeilen dasselbe bedeuten und dass man quer vergleichen will. Niemand
 * vergleicht „Cousin" mit „Grossvater".
 */

/** Ab wie vielen Eintraegen eine Gruppe zugeklappt beginnt. */
const SICHTBAR = 8;

type Gruppe = "vorfahren" | "seitenlinien" | "nachkommen" | "angeheiratet";

const TITEL: Record<Gruppe, string> = {
  vorfahren: "Vorfahren",
  seitenlinien: "Geschwister und Seitenlinien",
  nachkommen: "Nachkommen",
  angeheiratet: "Angeheiratet",
};

/**
 * In welche Gruppe jemand faellt. Die vier Faelle schliessen einander aus
 * und decken alles ab, was `entfernungen()` liefert.
 */
function gruppeVon(e: NonNullable<PersonKurz["entfernung"]>): Gruppe {
  if (e.schwieger >= 1) return "angeheiratet";
  if (e.abwaerts === 0 && e.aufwaerts >= 1) return "vorfahren";
  if (e.aufwaerts === 0 && e.abwaerts >= 1) return "nachkommen";
  return "seitenlinien";
}

/**
 * Ein Jahr, das nichts behauptet.
 *
 * `jahrKurz` schneidet die ersten vier Ziffern heraus - aus „vor 1900"
 * wird damit „1900", aus dem Zeitraum „1914-1918" wird „1914". Das ist in
 * einer Chronik keine Kuerzung, sondern eine Erfindung. Fuer diese drei
 * Genauigkeiten muss der Text stehen bleiben; sonst genuegt das Jahr
 * („1905", und bei „um" schreibt `jahrKurz` selbst die Tilde davor).
 */
function jahrKnapp(d: UnscharfesDatum | null): string {
  if (!d) return "";
  if (d.genauigkeit === "vor" || d.genauigkeit === "nach" || d.genauigkeit === "zeitraum") {
    return datumKurz(d);
  }
  return jahrKurz(d);
}

/** „1887–1954", „*1887", „†1954", „vor 1900" oder leer. */
function lebensspanne(p: PersonKurz): string {
  const geboren = jahrKnapp(datumAusSpalten(p, "geburt"));
  const gestorben = jahrKnapp(datumAusSpalten(p, "tod"));
  if (geboren && gestorben) return `${geboren}–${gestorben}`;
  // Das Sternchen nur vor eine Zahl. „*vor 1900" liest sich wie ein
  // Tippfehler, „vor 1900" sagt dasselbe und stimmt.
  const zeichen = (s: string, z: string) => (/^[\d~]/.test(s) ? z + s : s);
  if (geboren) return zeichen(geboren, "*");
  if (gestorben) return zeichen(gestorben, "†");
  return "";
}

function Zeile({
  p,
  ausloeser,
}: {
  p: PersonKurz;
  ausloeser: (p: PersonKurz) => Ausloeser;
}) {
  const jahre = lebensspanne(p);
  return (
    <Link
      to={`/person/${p.id}`}
      {...ausloeser(p)}
      className="flex items-center gap-2 rounded px-1 py-1 hover:bg-black/[0.04] dark:hover:bg-white/5"
    >
      <span className="h-6 w-6 shrink-0 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
        {p.portrait && (
          <img
            src={bildUrl(p.portrait.medienId, 64, p.portrait.tagId)}
            alt=""
            className="h-full w-full object-cover"
          />
        )}
      </span>
      <span className="truncate">
        {p.vorname} {p.famname}
      </span>
      <span className="ml-auto shrink-0 text-xs text-black/45 dark:text-white/45">
        {p.bezeichnung}
        {jahre && <span className="ml-2 tabular-nums">{jahre}</span>}
      </span>
    </Link>
  );
}

function Abschnitt({
  titel,
  leute,
  ausloeser,
}: {
  titel: string;
  leute: PersonKurz[];
  ausloeser: (p: PersonKurz) => Ausloeser;
}) {
  const [alle, setAlle] = useState(false);
  if (leute.length === 0) return null;

  const gezeigt = alle ? leute : leute.slice(0, SICHTBAR);

  return (
    <div className="mb-5">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">
        {titel}
        <span className="ml-2 font-normal normal-case tracking-normal">{leute.length}</span>
      </h3>
      <div className="grid gap-x-6 text-sm sm:grid-cols-2">
        {gezeigt.map((p) => (
          <Zeile key={p.id} p={p} ausloeser={ausloeser} />
        ))}
      </div>
      {leute.length > SICHTBAR && (
        <button
          onClick={() => setAlle(!alle)}
          className="mt-1 px-1 text-xs text-black/50 hover:underline dark:text-white/50"
        >
          {alle ? "weniger" : `… ${leute.length - SICHTBAR} weitere`}
        </button>
      )}
    </div>
  );
}

/** Stabil, damit das Memo unten nicht bei jedem Rendern neu rechnet. */
const KEINE_KANTEN: BaumDaten["kanten"] = [];

export function Verwandtschaft({
  personen,
  kanten = KEINE_KANTEN,
  umkreis,
  wurzel,
  laedt = false,
  fehler = false,
}: {
  personen: PersonKurz[];
  /**
   * Die Kanten aus derselben Antwort. Nur dafuer da, dass die Infokarte
   * „Eltern: … · 3 Kinder" zeigen kann - ohne sie fehlt bloss diese eine
   * Zeile.
   */
  kanten?: BaumDaten["kanten"];
  /** Der geladene Umkreis - siehe `umfeldKarte`. */
  umkreis?: number;
  wurzel: number;
  laedt?: boolean;
  fehler?: boolean;
}) {
  const umfeld = useMemo(
    () => umfeldKarte({ personen, kanten }, umkreis),
    [personen, kanten, umkreis],
  );
  const namen = useMemo(() => {
    const m = new Map(personen.map((p) => [p.id, `${p.vorname} ${p.famname}`.trim()]));
    return (id: number) => m.get(id) ?? null;
  }, [personen]);
  const karte = usePersonKarte(namen);
  const ausloeser = (p: PersonKurz) => karte.fuer(p, { umfeld: umfeld.get(p.id) });

  const gruppen: Record<Gruppe, PersonKurz[]> = {
    vorfahren: [],
    seitenlinien: [],
    nachkommen: [],
    angeheiratet: [],
  };

  for (const p of personen) {
    if (p.id === wurzel || !p.entfernung) continue;
    gruppen[gruppeVon(p.entfernung)].push(p);
  }

  /**
   * Innerhalb einer Gruppe erst nach Naehe, dann nach Geburt. Bei den
   * Nachkommen ergibt das von selbst die Reihenfolge, die eine Chronik
   * erwartet: die Kinder vom aeltesten zum juengsten, danach die Enkel.
   *
   * Nach `schwieger` zu sortieren waere wirkungslos - innerhalb einer
   * Gruppe ist der Wert konstant (die drei Blutsgruppen haben 0, die
   * angeheiratete hat 1, weil die Abfrage mit hoechstens einer
   * Partnerkante rechnet). Sortiert wird hier auf einer KOPIE, die oben
   * bei jedem Rendern frisch entsteht; `baum.data` wird nie angefasst.
   */
  const ordnen = (liste: PersonKurz[]) =>
    liste.sort(
      (a, b) =>
        a.entfernung!.blut - b.entfernung!.blut ||
        nachGeburt(a, b) ||
        `${a.famname} ${a.vorname}`.localeCompare(`${b.famname} ${b.vorname}`, "de"),
    );

  const reihenfolge: Gruppe[] = ["vorfahren", "seitenlinien", "nachkommen", "angeheiratet"];
  const leer = reihenfolge.every((g) => gruppen[g].length === 0);

  return (
    <section className="mt-8">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-black/50">
        Verwandtschaft
      </h2>

      {/*
        Erst laden, dann Fehler, dann leer. Ohne diese Reihenfolge stuende
        waehrend des Ladens „Noch niemand verknüpft" da - und bei einem
        Fehler bliebe es dauerhaft stehen, also eine falsche Aussage statt
        einer Fehlermeldung.
      */}
      {laedt ? (
        <p className="text-sm text-black/40">lädt …</p>
      ) : fehler ? (
        <p className="text-sm text-red-700">Die Verwandtschaft konnte nicht geladen werden.</p>
      ) : leer ? (
        <p className="text-sm text-black/50 dark:text-white/50">
          Noch niemand verknüpft. Über den Abschnitt „Familien“ kommen Eltern,
          Geschwister, Partner und Kinder dazu.
        </p>
      ) : (
        reihenfolge.map((g) => (
          <Abschnitt key={g} titel={TITEL[g]} leute={ordnen(gruppen[g])} ausloeser={ausloeser} />
        ))
      )}

      {karte.karte}
    </section>
  );
}
